import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Coffee,
  Copy,
  Gauge,
  Plus,
  Printer,
  Settings2,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import {
  useListExamGroupsQuery,
  useListSubjectsQuery,
  useListStaffQuery,
  useGetClassTimetableQuery,
  useGetMyTimetableQuery,
  useSetSlotMutation,
  useClearSlotMutation,
  getApiError,
  type Period,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/Spinner';
import { BellScheduleDialog } from './BellScheduleDialog';
import { CopyTimetableDialog } from './CopyTimetableDialog';
import { TeacherLoadDialog } from './TeacherLoadDialog';
import { TimetableSummary } from './TimetableSummary';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const DAYS = [
  { n: 1, label: 'Monday', short: 'Mon' },
  { n: 2, label: 'Tuesday', short: 'Tue' },
  { n: 3, label: 'Wednesday', short: 'Wed' },
  { n: 4, label: 'Thursday', short: 'Thu' },
  { n: 5, label: 'Friday', short: 'Fri' },
];

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE WEEKLY GRID

  Periods down, days across
  ─────────────────────────
  Not the other way round. A school day is read down a column — assembly, then
  period 1, then break — and that is the shape of the paper timetable already
  pinned up in every staff room. Turning it on its side makes people translate.

  Break and lunch are rows, not gaps
  ──────────────────────────────────
  A grid that skips them stops matching the day a child experiences: period 3
  appears directly under period 2 when there is twenty minutes between them.
  They are drawn as what they are — periods with nothing taught in them — and
  cannot be clicked.

  Why "My week" exists beside the class grid
  ──────────────────────────────────────────
  A teacher taking four classes cannot answer "where am I at 11:00" from any
  single class's grid; they would read four and assemble their own by hand.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function TimetablePage() {
  const navigate = useNavigate();
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const role = useAuthStore((s) => s.user?.role);
  const isOffice = role === 'institution_admin' || role === 'super_admin';
  const isTeacher = role === 'teacher';

  const [view, setView] = useState<'class' | 'mine'>(isTeacher ? 'mine' : 'class');
  const [groupId, setGroupId] = useState('');
  const [bellOpen, setBellOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [editing, setEditing] = useState<null | { day: number; period: Period }>(null);
  const [draft, setDraft] = useState({ subjectId: '', teacherId: '', room: '' });

  const { data: groups = [] } = useListExamGroupsQuery(institutionId, { skip: !institutionId });
  const { data: subjects = [] } = useListSubjectsQuery({ institutionId }, { skip: !institutionId });
  const { data: staff = [] } = useListStaffQuery(institutionId, { skip: !institutionId || !isOffice });

  useEffect(() => {
    if (!groupId && groups.length > 0) setGroupId(groups[0]!._id);
  }, [groups, groupId]);

  const classQuery = useGetClassTimetableQuery(
    { institutionId, groupId },
    { skip: !institutionId || !groupId || view !== 'class' },
  );
  const mineQuery = useGetMyTimetableQuery(institutionId, {
    skip: !institutionId || view !== 'mine',
  });

  const [setSlot, saveState] = useSetSlotMutation();
  const [clearSlot, clearState] = useClearSlotMutation();

  const data = view === 'class' ? classQuery.data : mineQuery.data;
  const periods = data?.periods ?? [];
  const loading = view === 'class' ? classQuery.isLoading : mineQuery.isLoading;

  /* Keyed by day:period so a cell is one lookup rather than a scan per cell. */
  const byCell = useMemo(() => {
    const map = new Map<string, (typeof slots)[number]>();
    const slots = data?.slots ?? [];
    for (const s of slots) map.set(`${s.day}:${s.periodId}`, s);
    return map;
  }, [data]);

  const teachers = staff.filter((s) => s.role === 'teacher' && s.isActive);
  const liveSubjects = subjects.filter((s) => s.isActive);
  const canEdit = view === 'class' && Boolean(groupId) && (isOffice || isTeacher);
  const error = saveState.error ?? clearState.error;

  const open = (day: number, period: Period) => {
    if (!canEdit || !period.isTeaching) return;
    const existing = byCell.get(`${day}:${period._id}`);
    setDraft({
      subjectId: existing?.subjectId ?? liveSubjects[0]?._id ?? '',
      teacherId: existing?.teacherId ?? '',
      room: existing?.room ?? '',
    });
    setEditing({ day, period });
  };

  const save = async () => {
    if (!editing) return;
    const result = await setSlot({
      institutionId,
      groupId,
      day: editing.day,
      periodId: editing.period._id,
      subjectId: draft.subjectId,
      /* Empty means "not staffed yet", which is a real state in August — not
         the same as picking somebody arbitrary. */
      teacherId: draft.teacherId || null,
      room: draft.room,
    });
    if (!('error' in result)) setEditing(null);
  };

  return (
    <PageLayout
      title="Timetable"
      subtitle="The weekly schedule each class and teacher runs to"
      breadcrumbs={[{ label: 'Academics' }, { label: 'Timetable' }]}
      contained={false}
      actions={
        <>
          {/* Paper first in the list, because it is what a timetable is FOR:
              it ends up on a classroom wall, and a module nobody can pin up
              gets re-typed into Word — after which the app's copy is the one
              that goes stale. */}
          {view === 'class' && groupId && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/timetable/print?groupId=${groupId}`)}
            >
              <Printer size={13} className="mr-1" />
              Print
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" size="sm" onClick={() => setCopyOpen(true)}>
              <Copy size={13} className="mr-1" />
              Copy
            </Button>
          )}
          {isOffice && (
            <Button variant="secondary" size="sm" onClick={() => setLoadOpen(true)}>
              <Gauge size={13} className="mr-1" />
              Teaching load
            </Button>
          )}
          {isOffice && (
            <Button variant="secondary" size="sm" onClick={() => setBellOpen(true)}>
              <Settings2 size={13} className="mr-1" />
              Bell schedule
            </Button>
          )}
        </>
      }
      toolbar={
        <>
          {view === 'class' && (
            <Select aria-label="Class" className="w-[190px]" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g) => (
                <option key={g._id} value={g._id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
          <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
            {([
              { v: 'class' as const, label: 'By class' },
              { v: 'mine' as const, label: 'My week' },
            ]).map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'rounded px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] transition-colors',
                  view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
          >
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
            {getApiError(error)}
          </div>
        )}

        {isOffice && liveSubjects.length === 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3.5 py-3">
            <TriangleAlert size={15} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
            <p className="text-[11.5px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
              No subjects yet — a timetable is built from them. Add them under Exam results first.
            </p>
          </div>
        )}

        {/* Only for a class: a teacher's own week has no "is it complete"
            question — their free periods are not holes to be filled. */}
        {view === 'class' && classQuery.data?.summary && !loading && (
          <TimetableSummary summary={classQuery.data.summary} />
        )}

        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : periods.length === 0 ? (
            <p className="px-3 py-10 text-center text-[11.5px] text-muted-foreground">
              No periods set. Open the bell schedule to build the school day.
            </p>
          ) : (
            <table className="w-full min-w-[720px] border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-border">
                  <th className={cn(styles.label, 'w-[130px] px-3 py-2 text-left')}>Period</th>
                  {DAYS.map((d) => (
                    <th key={d.n} className={cn(styles.label, 'px-3 py-2 text-left')}>
                      {d.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p._id} className={cn('border-b border-border last:border-b-0', !p.isTeaching && 'bg-muted/40')}>
                    <td className="px-3 py-1.5 align-top">
                      <p className="font-semibold text-foreground">{p.name}</p>
                      <p className={cn(styles.meta, 'tabular-nums')}>
                        {p.startTime}–{p.endTime}
                      </p>
                    </td>

                    {/* A non-teaching period spans the week: it is the same
                        twenty minutes for everybody, and five identical cells
                        would read as five separate things. */}
                    {!p.isTeaching ? (
                      <td colSpan={DAYS.length} className="px-3 py-2">
                        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
                          <Coffee size={12} />
                          {p.name}
                        </span>
                      </td>
                    ) : (
                      DAYS.map((d) => {
                        const slot = byCell.get(`${d.n}:${p._id}`);
                        const withClass = slot as (typeof slot & { className?: string }) | undefined;
                        return (
                          <td key={d.n} className="px-1.5 py-1.5 align-top">
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => open(d.n, p)}
                              aria-label={
                                slot
                                  ? `${slot.subject} on ${d.label}, ${p.name}`
                                  : `Add a lesson on ${d.label}, ${p.name}`
                              }
                              className={cn(
                                'w-full rounded-md border px-2 py-1.5 text-left transition-colors',
                                slot
                                  ? 'border-border bg-muted/50 hover:bg-muted'
                                  : 'border-dashed border-border/70 hover:bg-muted/40',
                                !canEdit && 'cursor-default hover:bg-transparent',
                              )}
                            >
                              {slot ? (
                                <>
                                  <span className="block truncate text-[11.5px] font-bold text-foreground">
                                    {slot.subject}
                                  </span>
                                  <span className={cn(styles.meta, 'block truncate')}>
                                    {view === 'mine'
                                      ? withClass?.className
                                      : slot.teacher || 'Not staffed'}
                                    {slot.room && ` · ${slot.room}`}
                                  </span>
                                </>
                              ) : (
                                <span className="flex items-center gap-1 text-[11px] text-muted-foreground/60">
                                  {canEdit && <Plus size={11} />}
                                  {canEdit ? 'Add' : '—'}
                                </span>
                              )}
                            </button>
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <p className={styles.meta}>
          <Clock size={11} className="mr-1 inline" />
          {view === 'mine'
            ? 'Every class you take, in one week.'
            : canEdit
              ? 'Tap a slot to set the subject, the teacher and the room. A teacher already due elsewhere is refused. Fill one day, then Copy it across the week.'
              : 'Read-only — the office and the class teacher set this.'}
        </p>
      </div>

      {/* ── One lesson ───────────────────────────────────────────────────── */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `${DAYS.find((d) => d.n === editing.day)?.label} · ${editing.period.name}` : 'Lesson'}
        description={editing ? `${editing.period.startTime}–${editing.period.endTime}` : undefined}
        footer={
          <>
            {editing && byCell.has(`${editing.day}:${editing.period._id}`) && (
              <Button
                variant="ghost"
                className="mr-auto text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
                loading={clearState.isLoading}
                onClick={() => {
                  void clearSlot({ institutionId, groupId, day: editing.day, periodId: editing.period._id });
                  setEditing(null);
                }}
              >
                <Trash2 size={13} className="mr-1" />
                Clear
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saveState.isLoading}
              disabled={!draft.subjectId}
              onClick={() => void save()}
            >
              Save lesson
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Subject" htmlFor="slot-subject" required>
            <Select
              id="slot-subject"
              value={draft.subjectId}
              onChange={(e) => setDraft({ ...draft, subjectId: e.target.value })}
            >
              {liveSubjects.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Teacher"
            htmlFor="slot-teacher"
            hint="Leave blank if staffing is not settled — an invented assignment looks decided."
          >
            <Select
              id="slot-teacher"
              value={draft.teacherId}
              onChange={(e) => setDraft({ ...draft, teacherId: e.target.value })}
            >
              <option value="">Not staffed yet</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.fullName}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Room" htmlFor="slot-room">
            <Input
              id="slot-room"
              placeholder="Room 4"
              maxLength={40}
              value={draft.room}
              onChange={(e) => setDraft({ ...draft, room: e.target.value })}
            />
          </Field>
        </div>
      </Modal>

      <CopyTimetableDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        institutionId={institutionId}
        groupId={groupId}
        className={groups.find((g) => g._id === groupId)?.name ?? 'this class'}
        groups={groups}
        grid={classQuery.data}
      />

      <TeacherLoadDialog open={loadOpen} onClose={() => setLoadOpen(false)} institutionId={institutionId} />

      <BellScheduleDialog open={bellOpen} onClose={() => setBellOpen(false)} institutionId={institutionId} />
    </PageLayout>
  );
}
